import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { choiceGroupsKey as productChoiceGroupsKey } from '@/features/products'

import {
  createChoiceGroup,
  createChoiceGroupItem,
  deleteChoiceGroup,
  deleteChoiceGroupItem,
  fetchChoiceGroup,
  fetchChoiceGroups,
  sortChoiceGroups,
  updateChoiceGroup,
  updateChoiceGroupItem,
} from '../api'
import type { ChoiceGroupItem, CreateChoiceGroupBody, SaveChoiceGroupItemBody } from '../types'

export const choiceGroupsKey = ['choice-groups'] as const
export const choiceGroupKey = (id: string) => [...choiceGroupsKey, 'detail', id] as const

export function useChoiceGroupList() {
  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: [...choiceGroupsKey, 'list'],
    queryFn: fetchChoiceGroups,
  })

  return {
    groups: data ?? [],
    isLoading: isPending,
    isRefreshing: isFetching && !isPending,
    error,
    retry: () => void refetch(),
  }
}

/** One group with its items. Idle until an id is given. */
export function useChoiceGroup(id: string | null) {
  const { data, isPending, error } = useQuery({
    queryKey: choiceGroupKey(id ?? ''),
    queryFn: () => fetchChoiceGroup(id!),
    enabled: Boolean(id),
  })

  return { group: data ?? null, isLoading: Boolean(id) && isPending, error }
}

/** An item as the form holds it: no id until the server has given it one. */
export interface ChoiceGroupItemDraft {
  id?: string
  name: { en: string | null; km: string | null }
  price: number
}

export interface SaveChoiceGroupInput {
  id: string | null
  group: CreateChoiceGroupBody
  items: ChoiceGroupItemDraft[]
  /** The items the group had before this edit, for working out what was removed. */
  originalItems: ChoiceGroupItem[]
}

export function useChoiceGroupActions() {
  const queryClient = useQueryClient()

  /**
   * The group list here and the product form's picker are the same data under
   * two cache keys, so a rename has to move both.
   */
  function invalidate() {
    return Promise.all([
      queryClient.invalidateQueries({ queryKey: choiceGroupsKey }),
      queryClient.invalidateQueries({ queryKey: productChoiceGroupsKey }),
    ])
  }

  /**
   * Creating is one call; editing is several.
   *
   * The update endpoint is built from the *read* DTO and takes no items, so an
   * edit has to reconcile them one at a time: added rows are posted, changed
   * rows are put, and rows the merchant removed are deleted. Deletions run first
   * — a group with a unique-name constraint would otherwise reject a rename that
   * swaps two items around.
   */
  const save = useMutation({
    mutationFn: async ({ id, group, items, originalItems }: SaveChoiceGroupInput) => {
      if (!id) {
        await createChoiceGroup({ ...group, items: items.map(toItemBody) })
        return
      }

      await updateChoiceGroup(id, {
        name: group.name,
        selectType: group.selectType,
        isRequire: group.isRequire,
        sortOrder: group.sortOrder,
      })

      const keptIds = new Set(items.map((item) => item.id).filter(Boolean))

      for (const original of originalItems) {
        if (!keptIds.has(original.id)) await deleteChoiceGroupItem(id, original.id)
      }

      for (const [index, item] of items.entries()) {
        const body = toItemBody(item, index)

        if (item.id) await updateChoiceGroupItem(id, item.id, body)
        else await createChoiceGroupItem(id, body)
      }
    },
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => deleteChoiceGroup(id),
    onSuccess: invalidate,
  })

  const reorder = useMutation({
    mutationFn: (itemIds: string[]) => sortChoiceGroups(itemIds),
    onSuccess: invalidate,
  })

  return { save, remove, reorder }
}

function toItemBody(item: ChoiceGroupItemDraft, index = 0): SaveChoiceGroupItemBody {
  return { name: item.name, price: item.price, sortOrder: index + 1 }
}
